
import { useState, useEffect } from 'react';
import { useCrypto } from '../../hooks/useCrypto';
import Header from '../../components/feature/Header';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Card from '../../components/base/Card';

const Convert = () => {
  const { cryptoData, loading, convertCrypto, walletBalances, transactions } = useCrypto();
  const [fromAsset, setFromAsset] = useState('bitcoin');
  const [toAsset, setToAsset] = useState('ethereum');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [isConverting, setIsConverting] = useState(false);

  // Filter conversion history from transactions
  const conversionHistory = transactions.filter(tx => tx.type === 'convert').slice(0, 10);

  const fromCoin = cryptoData.find(coin => coin.id === fromAsset);
  const toCoin = cryptoData.find(coin => coin.id === toAsset);
  const fromBalance = walletBalances.find(balance => balance.asset === fromAsset);
  const toBalance = walletBalances.find(balance => balance.asset === toAsset);

  // Calculate conversion rate and amounts
  useEffect(() => {
    if (fromCoin && toCoin && fromAmount) {
      const rate = fromCoin.current_price / toCoin.current_price;
      const convertedAmount = parseFloat(fromAmount) * rate;
      const fee = convertedAmount * 0.001; // 0.1% fee
      const finalAmount = convertedAmount - fee;
      setToAmount(finalAmount.toFixed(8));
    } else if (!fromAmount) {
      setToAmount('');
    }
  }, [fromAmount, fromAsset, toAsset, fromCoin, toCoin]);

  // Handle reverse calculation when toAmount changes
  const handleToAmountChange = (value: string) => {
    setToAmount(value);
    if (fromCoin && toCoin && value) {
      const rate = toCoin.current_price / fromCoin.current_price;
      const convertedAmount = parseFloat(value) * rate;
      const fee = convertedAmount * 0.001;
      const finalAmount = convertedAmount + fee;
      setFromAmount(finalAmount.toFixed(8));
    } else if (!value) {
      setFromAmount('');
    }
  };

  const handleSwapAssets = () => {
    const tempAsset = fromAsset;
    const tempAmount = fromAmount;
    setFromAsset(toAsset);
    setToAsset(tempAsset);
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  const handleConvert = async () => {
    if (!fromAmount || !toAmount) {
      alert('Please enter an amount to convert');
      return;
    }

    const fromAmountNum = parseFloat(fromAmount);
    const toAmountNum = parseFloat(toAmount);
    
    if (fromBalance && fromAmountNum > fromBalance.balance) {
      alert('Insufficient balance');
      return;
    }

    if (!fromCoin || !toCoin) {
      alert('Invalid asset selection');
      return;
    }

    setIsConverting(true);
    
    try {
      const rate = fromAmountNum > 0 ? toAmountNum / fromAmountNum : 0;
      const success = convertCrypto(
        fromCoin.symbol,
        toCoin.symbol,
        fromAmountNum,
        toAmountNum,
        rate
      );
      
      if (success) {
        alert(`Successfully converted ${fromAmountNum} ${fromCoin.symbol.toUpperCase()} to ${toAmountNum.toFixed(6)} ${toCoin.symbol.toUpperCase()}`);
        setFromAmount('');
        setToAmount('');
      } else {
        alert('Conversion failed. Please check your balance and try again.');
      }
    } catch (error) {
      alert('Conversion failed. Please try again.');
    } finally {
      setIsConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading conversion data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Convert Crypto</h1>
          <p className="text-gray-600">Instantly convert between different cryptocurrencies</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversion Interface */}
          <div className="lg:col-span-2">
            <Card className="p-8">
              <h3 className="text-2xl font-semibold mb-8">Convert Cryptocurrency</h3>
              
              <div className="space-y-6">
                {/* From Section */}
                <div className="bg-gray-50 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-gray-700">From</label>
                    <span className="text-sm text-gray-600">
                      Balance: {fromBalance?.balance.toFixed(6) || '0.000000'} {fromCoin?.symbol.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <Input
                        type="number"
                        value={fromAmount}
                        onChange={(e) => setFromAmount(e.target.value)}
                        placeholder="0.00"
                        className="text-xl font-semibold h-14"
                      />
                    </div>
                    
                    <div className="w-48">
                      <select
                        value={fromAsset}
                        onChange={(e) => setFromAsset(e.target.value)}
                        className="w-full h-14 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-medium pr-8"
                      >
                        {cryptoData.slice(0, 20).map((coin) => (
                          <option key={coin.id} value={coin.id}>
                            {coin.symbol.toUpperCase()} - {coin.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {fromCoin && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center space-x-2">
                        <img src={fromCoin.image} alt={fromCoin.name} className="w-6 h-6" />
                        <span className="font-medium">{fromCoin.name}</span>
                      </div>
                      <span className="text-gray-600">
                        ${fromCoin.current_price.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Swap Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleSwapAssets}
                    className="w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-up-down-line text-xl"></i>
                  </button>
                </div>

                {/* To Section */}
                <div className="bg-gray-50 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-gray-700">To</label>
                    <span className="text-sm text-gray-600">
                      Balance: {toBalance?.balance.toFixed(6) || '0.000000'} {toCoin?.symbol.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <Input
                        type="number"
                        value={toAmount}
                        onChange={(e) => handleToAmountChange(e.target.value)}
                        placeholder="0.00"
                        className="text-xl font-semibold h-14"
                      />
                    </div>
                    
                    <div className="w-48">
                      <select
                        value={toAsset}
                        onChange={(e) => setToAsset(e.target.value)}
                        className="w-full h-14 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-medium pr-8"
                      >
                        {cryptoData.slice(0, 20).map((coin) => (
                          <option key={coin.id} value={coin.id}>
                            {coin.symbol.toUpperCase()} - {coin.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {toCoin && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center space-x-2">
                        <img src={toCoin.image} alt={toCoin.name} className="w-6 h-6" />
                        <span className="font-medium">{toCoin.name}</span>
                      </div>
                      <span className="text-gray-600">
                        ${toCoin.current_price.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Conversion Details */}
                {fromAmount && toAmount && fromCoin && toCoin && (
                  <div className="bg-blue-50 p-6 rounded-xl">
                    <h4 className="font-semibold text-blue-900 mb-4">Conversion Details</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Exchange Rate:</span>
                        <span className="font-medium text-blue-900">
                          1 {fromCoin.symbol.toUpperCase()} = {(fromCoin.current_price / toCoin.current_price).toFixed(6)} {toCoin.symbol.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Network Fee:</span>
                        <span className="font-medium text-blue-900">
                          {(parseFloat(toAmount) * 0.001).toFixed(8)} {toCoin.symbol.toUpperCase()} (0.1%)
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">You'll Receive:</span>
                        <span className="font-bold text-blue-900 text-lg">
                          {parseFloat(toAmount).toFixed(6)} {toCoin.symbol.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Convert Button */}
                <Button
                  onClick={handleConvert}
                  disabled={!fromAmount || !toAmount || isConverting}
                  className="w-full h-14 text-lg font-semibold bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isConverting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Converting...
                    </div>
                  ) : (
                    'Convert Now'
                  )}
                </Button>
              </div>
            </Card>
          </div>

          {/* Market Rates & History */}
          <div className="lg:col-span-1 space-y-6">
            {/* Popular Pairs */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Popular Pairs</h3>
              <div className="space-y-3">
                {[
                  { from: 'BTC', to: 'ETH', rate: 16.4, change: 2.3 },
                  { from: 'ETH', to: 'BNB', rate: 6.25, change: -1.2 },
                  { from: 'BNB', to: 'ADA', rate: 170.0, change: 5.7 },
                  { from: 'ADA', to: 'SOL', rate: 0.024, change: -3.1 },
                  { from: 'SOL', to: 'BTC', rate: 0.0018, change: 1.8 },
                ].map((pair, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      const fromCoin = cryptoData.find(coin => coin.symbol.toUpperCase() === pair.from);
                      const toCoin = cryptoData.find(coin => coin.symbol.toUpperCase() === pair.to);
                      if (fromCoin && toCoin) {
                        setFromAsset(fromCoin.id);
                        setToAsset(toCoin.id);
                      }
                    }}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div>
                      <p className="font-medium">{pair.from}/{pair.to}</p>
                      <p className="text-sm text-gray-600">{pair.rate.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm ${pair.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {pair.change >= 0 ? '+' : ''}{pair.change.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Quick Convert */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Convert</h3>
              <div className="space-y-3">
                {['0.1', '0.5', '1.0', '2.0'].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setFromAmount(amount)}
                    className="w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {amount} {fromCoin?.symbol.toUpperCase()}
                  </button>
                ))}
                <button
                  onClick={() => fromBalance && setFromAmount(fromBalance.balance.toString())}
                  className="w-full p-3 text-left bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Max: {fromBalance?.balance.toFixed(6)} {fromCoin?.symbol.toUpperCase()}
                </button>
              </div>
            </Card>
          </div>
        </div>

        {/* Conversion History */}
        <Card className="p-6 mt-8">
          <h3 className="text-xl font-semibold mb-6">Conversion History</h3>
          {conversionHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3">Date</th>
                    <th className="text-left py-3">From</th>
                    <th className="text-left py-3">To</th>
                    <th className="text-left py-3">Rate</th>
                    <th className="text-left py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionHistory.map((conversion) => (
                    <tr key={conversion.id} className="border-b">
                      <td className="py-4 text-sm text-gray-600">
                        {new Date(conversion.date).toLocaleString()}
                      </td>
                      <td className="py-4">
                        <div className="font-medium">
                          {conversion.amount} {conversion.fromAsset}
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="font-medium">
                          {conversion.toAmount?.toFixed(6)} {conversion.toAsset}
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="text-sm">
                          1 {conversion.fromAsset} = {conversion.toAmount && conversion.amount ? (conversion.toAmount / conversion.amount).toFixed(6) : '0'} {conversion.toAsset}
                        </div>
                      </td>
                      <td className="py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          conversion.status === 'completed' ? 'bg-green-100 text-green-800' :
                          conversion.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {conversion.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <i className="ri-exchange-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">No conversion history yet</p>
              <p className="text-sm text-gray-500">Your conversions will appear here</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Convert;